import {
    IonContent,
    IonPage,
    IonSkeletonText,
    IonIcon,
    IonLabel,
    IonCard,
    IonItem,
    IonList,
    IonBadge,
    IonListHeader,
    IonThumbnail,
    IonImg,
    useIonAlert,
    IonCardContent,
    IonHeader,
    IonToolbar,
    IonButton,
    IonInput,
    IonInfiniteScroll, 
    IonInfiniteScrollContent,
    useIonModal,
    useIonToast,
    IonText,
    IonRefresher,
    IonRefresherContent,
    RefresherEventDetail
} from '@ionic/react';
import React, { useState } from 'react';
import { storefrontOutline, searchOutline, arrowBackOutline, addCircleOutline, warningOutline, personCircleOutline, documentTextOutline, chevronDownCircleOutline } from 'ionicons/icons';
import { RestApi } from '../services/Api';
import Detail from '../components/so/Detail';
import { loadList } from '../models/Data';
import { useTranslation } from 'react-i18next';
import { getNameDate, getRoleMenu, Rupiah, useConstructor } from '../services/Library';
import Form from '../components/so/Form';
import { useSelector } from 'react-redux';
import './So.scss';

const So: React.FC = () => {
  const [presentToast] = useIonToast();
  const { t } = useTranslation(["general"]);
  const [alert] = useIonAlert();
  const [isInfiniteDisabled, setInfiniteDisabled] = useState(false);
  const user = useSelector((state: any) => state.user.value);
  const getRole = getRoleMenu(user.role_menu, "so");
  const [getIdSo, setGetIdSo] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [loaded, setLoaded] = useState<boolean>(false);
  const [listSo, setListSo] = useState<any>([]);
  const [dataSo, setDataSo] = useState<any>([]);

  const status: any = [
    {
      name: t("open"),
      color: "success",
    },
    {
      name: t("done"),
      color: "danger",
    },
    {
      name: t("check"),
      color: "secondary",
    },
    {
      name: t("wait-approve"),
      color: "primary",
    },
    {
      name: t("cancel"),
      color: "warning",
    },
  ];

  const handleDismissDetail = (e: any) => {
    dismissDetail();
  };

  const [presentDetail, dismissDetail] = useIonModal(Detail, {
    idSo: getIdSo,
    onDismiss: handleDismissDetail,
  });

  const massageAlert = async (status: string, body: string) => {
    alert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [{ cssClass: "alert-button-confirm", text: "Ok" }],
    });
  };

  const getSoList = async (page: string, next: boolean) => {
    await RestApi(page, "get", {}, "application/json")
      .then((res: any) => {
        if (res.data.status) {
          setDataSo(res.data.data);
          if (next) {
            setListSo((prevState: any) => [
              ...prevState,
              ...res.data.data.data,
            ]);
          } else {
            setListSo(res.data.data.data);
          }
        } else {
          setListSo([]);
          setDataSo([]);
          presentToast({
            message: res.data.message,
            duration: 2500,
            color: "danger",
            icon: warningOutline,
          });          
        }
        setLoaded(true);
      })
      .catch((err: any) => {
        massageAlert('error', err.message);
        setLoaded(true);
      });
  };

  const handleDismissForm = () => {
    dismissForm();
    getSoList(`so?page=1&search=${searchText}`, false);
  };

  const [presentForm, dismissForm] = useIonModal(Form, {
    idSo: getIdSo,
    type: "create",
    idVisit: "",
    idCustomer: "",
    onDismiss: handleDismissForm,
  });

  const getSoDetail = async (id: string) => {
    setGetIdSo(id);
    presentDetail({
      cssClass: "modal-fullscreen",
    });
  };
  
  const loadData = (ev: any) => {
    if (dataSo.current_page === dataSo.last_page) {
      setInfiniteDisabled(true);
      return;
    }
    getSoList(
      `so?page=${dataSo.current_page + 1}&search=${searchText}`,
      true
    );
    ev.target.complete();
  };

  const onChangeSearch = (e: any) => {
    setSearchText(e.detail.value!);
    getSoList(`so?page=1&search=${e.detail.value!}`, false);
  };

  const doRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    getSoList(`so?page=1&search=${searchText}`, false);
    event.detail.complete();
  };
  
  useConstructor(() => {
    getSoList(`so?page=1&search=${searchText}`, false);
  });

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="toolbar-so">
          <IonButton
            color="light"
            slot="start"
            fill="clear"
            routerLink="/home"
            routerDirection="back"
            className=""
            size="small"
          >
            <IonIcon color="light" src={arrowBackOutline} />
          </IonButton>
          <div className="search" slot="end">
            <IonItem lines="none" className="item-so">
              <IonInput
                placeholder={t("search") + " " + t("module.so")}
                className="input-so"
                onIonInput={(e) => onChangeSearch(e)}
              ></IonInput>
              <IonIcon src={searchOutline} slot="start" />
              <IonBadge color="medium">{dataSo.total}</IonBadge>
            </IonItem>
          </div>
          {getRole?.role_menu_add === 1 && (
            <IonButton
              fill="clear"
              slot="end"
              color="light"
              className=""
              onClick={() => {
                presentForm({
                  cssClass: "modal-fullscreen",
                });
              }}
            >
              <IonIcon
                size="small"
                className="ion-no-margin ion-no-padding"
                src={addCircleOutline}
              ></IonIcon>
              {t("create")}
            </IonButton>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent className="so-content">
        {!loaded && (
          <IonList>
            <IonListHeader>
              <IonSkeletonText
                animated={true}
                style={{ width: "80px" }}
              ></IonSkeletonText>
            </IonListHeader>
            {loadList.map((value: any, key: any) => (
              <IonItem key={key}>
                <IonThumbnail slot="start">
                  <IonSkeletonText animated={true}></IonSkeletonText>
                </IonThumbnail>
                <IonLabel>
                  <h3>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "80%" }}
                    ></IonSkeletonText>
                  </h3>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "60%" }}
                    ></IonSkeletonText>
                  </p>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "30%" }}
                    ></IonSkeletonText>
                  </p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t("loading") ?? ""}
            refreshingSpinner="circles"
            refreshingText={t("loading") ?? ""}
          ></IonRefresherContent>
        </IonRefresher>
        {listSo.length && loaded ? (
          <IonList className="main-card">
            {listSo.map((value: any, key: any) => (
              <IonCard
                className="ion-padding-top"
                key={key}
                onClick={() => {
                  getSoDetail(value.so_id);
                }}
              >
                <IonItem>
                  <IonText className="ion-no-padding ion-no-margin">
                    <p className="ion-no-padding ion-no-margin">
                      <span>
                        <IonIcon size="small" src={storefrontOutline} />
                        {value.so_customer_name + " "}
                        <IonBadge color="dark">{value.so_customer_id}</IonBadge>
                      </span>
                      <br />
                      <span>
                        <IonIcon
                          size="small"
                          className="ion-no-padding"
                          src={personCircleOutline}
                        />
                        {value.salesman_name}
                      </span>
                    </p>
                  </IonText>
                  <IonLabel
                    className="ion-no-padding ion-no-margin ion-text-end"
                    slot="end"
                  >
                    <p className="ion-no-padding ion-no-margin">
                      <span className="ion-no-padding ion-no-margin">
                        <IonIcon
                          size="small"
                          style={{
                            marginRight: "2px",
                          }}
                          src={documentTextOutline}
                        />
                        <strong>{value.so_id}</strong>
                      </span>
                      <br />
                      <span className="ion-no-padding ion-no-margin">
                        <IonBadge
                          className="ion-float-end"
                          color={status[value.so_is_closed].color}
                        >
                          {status[value.so_is_closed].name}
                        </IonBadge>
                      </span>
                    </p>
                  </IonLabel>
                </IonItem>
                <IonCardContent className="ion-no-padding">
                  <IonItem lines="none">
                    <IonText className="ion-no-padding">
                      <p>{value.term_name}</p>
                      <p>{Rupiah(value.so_total_amount)}</p>
                    </IonText>
                    <IonText slot="end" className="ion-no-padding ion-text-end">
                      <p>Tanggal : {getNameDate(value.so_date)}</p>
                      <p>
                        Tgl. Perkiraan : {getNameDate(value.so_expected_date)}
                      </p>
                    </IonText>
                  </IonItem>
                </IonCardContent>
              </IonCard>
            ))}
          </IonList>
        ) : (
          <IonImg className="img-empty" src={"assets/img/search-empty.png"} />
        )}
        <IonInfiniteScroll
          onIonInfinite={loadData}
          threshold="100px"
          disabled={isInfiniteDisabled}
        >
          {dataSo.next_page_url && (
            <IonInfiniteScrollContent
              loadingSpinner="bubbles"
              loadingText="Loading more data..."
            >
              <IonItem>
                <IonThumbnail slot="start">
                  <IonSkeletonText animated={true}></IonSkeletonText>
                </IonThumbnail>
                <IonLabel>
                  <h3>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "80%" }}
                    ></IonSkeletonText>
                  </h3>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "60%" }}
                    ></IonSkeletonText>
                  </p>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "30%" }}
                    ></IonSkeletonText>
                  </p>
                </IonLabel>
              </IonItem>
            </IonInfiniteScrollContent>
          )}
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default So;
